import type { ReviewSignalBundle } from './reviewSignalBundle.types';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function coerceBundle(raw: Record<string, unknown>): ReviewSignalBundle | null {
  const blocking = Array.isArray(raw.blockingSignals) ? raw.blockingSignals : [];
  const advisory = Array.isArray(raw.advisorySignals) ? raw.advisorySignals : [];
  if (blocking.length === 0 && advisory.length === 0 && !raw.verificationId && !raw.finalStatus) {
    return null;
  }

  return {
    schemaVersion: typeof raw.schemaVersion === 'string' ? raw.schemaVersion : undefined,
    verificationId: typeof raw.verificationId === 'string' ? raw.verificationId : undefined,
    applicationId: typeof raw.applicationId === 'string' ? raw.applicationId : undefined,
    attemptNumber:
      typeof raw.attemptNumber === 'number' && Number.isFinite(raw.attemptNumber)
        ? raw.attemptNumber
        : undefined,
    finalStatus: typeof raw.finalStatus === 'string' ? raw.finalStatus : undefined,
    verdict: typeof raw.verdict === 'string' ? raw.verdict : undefined,
    blockingSignals: blocking as ReviewSignalBundle['blockingSignals'],
    advisorySignals: advisory as ReviewSignalBundle['advisorySignals'],
    gateCheckpoints:
      raw.gateCheckpoints && typeof raw.gateCheckpoints === 'object' && !Array.isArray(raw.gateCheckpoints)
        ? (raw.gateCheckpoints as Record<string, unknown[]>)
        : undefined,
  };
}

/** Reads reviewSignalBundle from EV evidence, orchestrator lastActionResult, or nested payloads. */
export function extractReviewSignalBundle(sources: {
  evidence?: unknown;
  lastActionResult?: Record<string, unknown> | null;
  bundle?: unknown;
}): ReviewSignalBundle | null {
  if (sources.bundle) {
    const direct = asRecord(sources.bundle);
    if (direct) {
      const coerced = coerceBundle(direct);
      if (coerced) return coerced;
    }
  }

  const evidence = asRecord(sources.evidence);
  if (evidence) {
    const nested = asRecord(evidence.reviewSignalBundle) ?? evidence.reviewSignalBundle;
    const nestedRecord = asRecord(nested);
    if (nestedRecord) {
      const coerced = coerceBundle(nestedRecord);
      if (coerced) return coerced;
    }
  }

  const lastActionResult = sources.lastActionResult;
  if (lastActionResult) {
    const fromLast = asRecord(lastActionResult.reviewSignalBundle);
    if (fromLast) {
      const coerced = coerceBundle(fromLast);
      if (coerced) return coerced;
    }
  }

  return null;
}

export function blockingSignalHeadlines(bundle: ReviewSignalBundle, limit = 3): string[] {
  return bundle.blockingSignals
    .map((signal) => signal.message?.trim())
    .filter((message): message is string => Boolean(message))
    .slice(0, limit);
}

export function advisorySignalHeadlines(bundle: ReviewSignalBundle, limit = 3): string[] {
  return bundle.advisorySignals
    .map((signal) => signal.message?.trim())
    .filter((message): message is string => Boolean(message))
    .slice(0, limit);
}
